/**
 * ImpersonationBanner — fixed-top banner shown whenever the active session
 * is an admin impersonating someone else (per Sprint 3 portal decision).
 *
 * Detection is local: when the impersonate-as flow runs, the trigger stashes
 * the original admin token under `ml_token_original` and swaps the active
 * `ml_token` for the impersonated one. The banner reads the active token's
 * `impersonated_by_uid` claim to decide whether to render.
 *
 * Exit calls /admin/impersonate/exit (best-effort audit-log close), restores
 * the original token, and reloads. The reload is intentional — every page
 * caches auth-derived data; a reload is the simplest way to flush all of it.
 */
import React, { useEffect, useState } from "react"
import { tokens } from "@/design-system/tokens"
import { authedFetch, getToken, setToken } from '@/lib/api';
import { LogOut } from "lucide-react"

interface JwtPayload {
  uid?: number
  email?: string
  name?: string
  impersonated_by_uid?: number
}

function decodeJwt(token: string | null): JwtPayload | null {
  if (!token) return null
  const parts = token.split(".")
  if (parts.length !== 3) return null
  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    )
    return payload as JwtPayload
  } catch {
    return null
  }
}

export function ImpersonationBanner() {
  const [tick, setTick] = useState(0)
  // Re-read whenever localStorage changes (cross-tab) or whenever the user
  // navigates within the app (route change can imply a token swap).
  useEffect(() => {
    const onStorage = () => setTick(t => t + 1)
    window.addEventListener("storage", onStorage)
    return () => window.removeEventListener("storage", onStorage)
  }, [])

  const token = typeof window !== "undefined" ? getToken() : null
  const claims = decodeJwt(token)
  if (!claims?.impersonated_by_uid) return null

  const onExit = async () => {
    try {
      await authedFetch("/api/v1/admin/impersonate/exit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      }, { redirectOnAuthFailure: false })
    } catch { /* best-effort */ }
    const original = localStorage.getItem("ml_token_original")
    if (original) {
      // skipSideEffects: the Watch/native mirror never received the borrowed
      // token, so there is nothing to resync, and SSE reconnects via the
      // reload below. ml_token_exp IS updated for the restored admin token —
      // see setToken() in lib/api.ts (#2091).
      await setToken(original, { skipSideEffects: true })
      localStorage.removeItem("ml_token_original")
    } else {
      localStorage.removeItem("ml_token")
    }
    window.location.href = "/"
    void tick
  }

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
      padding: "8px 16px",
      background: "#7C2D12", color: "#FED7AA",
      fontFamily: tokens.typography.fontFamily.sans, fontSize: 13, fontWeight: 600,
      boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
      borderBottom: "1px solid #9A3412",
    }}>
      <span style={{ opacity: 0.9 }}>You are impersonating</span>
      <span style={{ color: "#FFFFFF" }}>{claims.name || claims.email || `user ${claims.uid}`}</span>
      <span style={{ opacity: 0.6, fontSize: 11 }}>· every action is audited</span>
      <button
        onClick={onExit}
        style={{
          marginLeft: 8, padding: "4px 12px", borderRadius: 6,
          background: "#FED7AA", color: "#7C2D12", border: "none",
          cursor: "pointer", fontSize: 12, fontWeight: 700,
          display: "inline-flex", alignItems: "center", gap: 4,
          fontFamily: tokens.typography.fontFamily.sans,
        }}
      >
        <LogOut size={12} /> Exit
      </button>
    </div>
  )
}
